import { useState } from 'react';
import { keys, entries } from 'lodash';
import {
    Text,
    Box,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Flex,
    Button,
    useToast,
    useDisclosure,
} from '@chakra-ui/react';
import {
    TGetAllClientProductsPayload,
    useAddClientProductV2Mutation,
    useUpdateClientProductV2Mutation,
} from 'core/API/productsApi';
import { useTranslation } from 'helpers/languages/translations';
import { linkStyle } from 'shared/styles';
import { IProductTitle, TClientProduct, TRetailerProp } from 'shared/types';
import { isFetchBaseQueryError } from 'utils/erros';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { defaultAliasesInputValue, MAX_NAME_SPLITS } from '../../consts';
import AttributeByType from './AttributeByType';

type TUpdateAndAddModalProps = {
    isOpen: boolean;
    modalType: string;
    productToUpdate: IProductTitle;
    retailer: TRetailerProp;
    lang: string;
    clientProducts?: TGetAllClientProductsPayload;
    isUpc?: boolean;
    readonly: boolean;
    setProductToUpdate: React.Dispatch<React.SetStateAction<IProductTitle>>;
    onClose: () => void;
    onDeleteOpen: () => void;
};

const UpdateAndAddModal = ({
    isOpen,
    modalType,
    productToUpdate,
    retailer,
    lang,
    clientProducts,
    isUpc,
    readonly,
    setProductToUpdate,
    onClose,
    onDeleteOpen,
}: TUpdateAndAddModalProps) => {
    const translate = useTranslation();
    const toast = useToast();

    const [addProduct, { isLoading: isAddLoading }] = useAddClientProductV2Mutation();
    const [updateProduct, { isLoading: isUpdateLoading }] = useUpdateClientProductV2Mutation();

    const [aliasesByFieldInput, setAliasesByFieldInput] = useState<Record<string, string>>(defaultAliasesInputValue);
    const { isOpen: isMore, onClose: onCloseBtn, onOpen: onOpenBtn } = useDisclosure();

    const handleUpdateProductTitle = async () => {
        try {
            const res =
                modalType === 'update'
                    ? await updateProduct({
                          product: productToUpdate,
                          isProductTitle: true,
                          retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                          lang,
                      })
                    : await addProduct({
                          product: productToUpdate,
                          isProductTitle: true,
                          isAliases: true,
                          retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                          lang,
                      });

            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate(`splits.${modalType}.error`) as string);
            }

            onClose();
            const isEanNotdefined =
                modalType === 'add' &&
                (res.data as { code: number; created: TClientProduct }).created.is_ean_defined === false;
            toast({
                title: isEanNotdefined
                    ? translate('product.add.successEanNotDefined', { ean: productToUpdate.ean })
                    : translate(`product.${modalType}.success`),
                status: isEanNotdefined ? 'warning' : 'success',
                isClosable: true,
            });
        } catch (err) {
            toast({
                title: (err as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleDelete = () => {
        onClose();
        onDeleteOpen();
    };

    const emptyNameSplits = () => {
        const tempProductToUpdate: IProductTitle = { ...productToUpdate };

        entries(productToUpdate).forEach((entry) => {
            if (!/^ean|upc|name$/.test(entry[0])) {
                tempProductToUpdate[entry[0]] = { val: '' };
            }
        });

        setAliasesByFieldInput(defaultAliasesInputValue);
        setProductToUpdate(tempProductToUpdate);
    };

    const attibuteProps = {
        productToUpdate,
        modalType,
        isUpc,
        aliasesByFieldInput,
        setAliasesByFieldInput,
        setProductToUpdate,
        aliases: clientProducts?.aliases || {},
    };

    const maxLength = (clientProducts?.splitsTitles.length || MAX_NAME_SPLITS) + 2;

    return (
        <Modal
            size="4xl"
            isOpen={isOpen}
            onClose={() => {
                onClose();
                onCloseBtn();
            }}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{translate(`splits.${modalType}.header`)}</ModalHeader>
                <ModalCloseButton isDisabled={isUpdateLoading || isAddLoading} mt="2" />
                <ModalBody>
                    {keys(defaultAliasesInputValue).map((key, idx) => (
                        <>
                            {idx === maxLength ? (
                                <Flex>
                                    <Button
                                        textTransform="uppercase"
                                        w="50%"
                                        onClick={onOpenBtn}
                                        display={isMore ? 'none' : 'auto'}
                                        m="auto"
                                        mt={2}
                                    >
                                        {translate('more')}
                                    </Button>
                                </Flex>
                            ) : null}
                            {idx < maxLength ? (
                                <AttributeByType
                                    type={key}
                                    {...attibuteProps}
                                    label={clientProducts?.splitsTitles[idx - 1]}
                                    readonly={readonly}
                                />
                            ) : null}
                        </>
                    ))}
                    {isMore
                        ? keys(defaultAliasesInputValue).map((key, idx) =>
                              idx >= maxLength ? (
                                  <AttributeByType
                                      type={key}
                                      {...attibuteProps}
                                      label={clientProducts?.splitsTitles[idx - 2]}
                                      readonly={readonly}
                                  />
                              ) : null,
                          )
                        : null}
                </ModalBody>
                <ModalFooter fontSize="sm" justifyContent="space-between">
                    {!readonly && (
                        <Flex>
                            {modalType === 'update' && (
                                <>
                                    <Box {...linkStyle} color="red.500" onClick={handleDelete}>
                                        {translate('product.update.delete')}
                                    </Box>
                                    <Text>/</Text>
                                </>
                            )}
                            <Box {...linkStyle} color="red.500" onClick={emptyNameSplits}>
                                {translate('splits.update.empty')}
                            </Box>
                        </Flex>
                    )}

                    <Flex flex="1" justifyContent="flex-end">
                        <Button
                            isDisabled={isUpdateLoading || isAddLoading}
                            size="sm"
                            variant="outline"
                            mr={3}
                            onClick={onClose}
                        >
                            {translate('product.delete.cancel')}
                        </Button>
                        {!readonly && (
                            <Button
                                onClick={intentionalyFloatingPromiseReturn(handleUpdateProductTitle)}
                                size="sm"
                                colorScheme="blue"
                                bg="blueMain"
                                isLoading={isUpdateLoading || isAddLoading}
                                isDisabled={modalType === 'add' && !productToUpdate.ean}
                            >
                                {translate(`splits.${modalType}.apply`)}
                            </Button>
                        )}
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default UpdateAndAddModal;
